import client, { fetch, post, put, remove } from '@/base-http'
import Pagination, { PaginationInput } from '@/modules/core/model/Pagination'
import Reservation from '../model/Reservation'
import KeyValuePair from '@/modules/core/support/KeyValuePair'
import Department from '../model/Department'
import DepartmentType from '../model/DepartmenType'
import Event from '../model/Event'
import _ from 'lodash'
import Service from '@/modules/reservation/model/Service';
import {async} from 'q';
import moment from 'moment'
import {CreateListResult} from '@/modules/reservation/model/CreateListResult';
import {ApiError} from '@/api';
import PrintResult from "@/modules/reservation/model/PrintResult";


export interface GetReservationPriceDescriptor {
    department: number
    services: number[],
    date: Date,
}

/**
 * Filtros para la paginación de reservas
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
export class ReservationPaginationFilters {
    /**
     * Búsqueda por coincidencia de nombre de usuario
     */
    public username = ''

    /**
     * Búsqueda por usuario
     */
    public userId = 0

    /**
     * Búsqueda por id de departamento
     */
    public department = 0

    /**
     * Búsqueda por fecha
     */
    public date: Date | null = null

    /**
     * Búsqueda por rango de fechas, especifica la fecha de inicio
     * del filtrado
     */
    public startDate: Date | null = null

    /**
     * Búsqueda por rango de fechas, especifica la fecha de fin del
     * filtrado
     */
    public endDate: Date | null = null

    /**
     * Búsqueda por id de reserva
     */
    public reservationId = 0

    /**
     * Filtrar por tipo de departamento
     */
    public departmentType = 0

    /**
     * Filtrar por ID del documento
     */
    public documentID = ''

    /**
     * Filtrar por pago
     */
    public payment = ''

    /**
     * Indica si la estructura esta limpia, sin ningún dato introducido por
     * el usuario
     */
    public get areClean (): boolean {
        return !this.username
            && !this.userId
            && !this.department
            && !this.date
            && !this.startDate
            && !this.endDate
            && !this.reservationId
            && !this.departmentType
            && !this.documentID
            && !this.payment
    }

    /**
     * Convierte la clase de filtrados en un objeto plano para ser utilizado
     * como parámetros
     */
    public serialize () {
        const params: KeyValuePair = {
            username: this.username || undefined,
            user_id: this.userId || undefined,
            department: this.department || undefined,
            reservation_id: this.reservationId || undefined,
            department_type: this.departmentType || undefined,
            document_id: this.documentID || undefined,
            payment: this.payment || undefined,
        }

        // Se ha declarado una fecha de filtrado, se convierte
        if (this.date) {
            params.date = moment(this.date).format('YYYY-MM-DD')
        }

        if (this.startDate) {
            params.date_start = moment(this.startDate).format('YYYY-MM-DD')
        }

        if (this.endDate) {
            params.date_end = moment(this.endDate).format('YYYY-MM-DD')
        }

        return params
    }
}

/**
 * Rango de fechas que permite representar el momento de inicio y final de
 * una reserva
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
export class ReservationDateRange {
    public static format = 'DD/MM/YYYY.HH:mm:ss'

    /**
     * Constructor
     *
     * @param startDate Fecha de inicio de la reserva
     * @param endDate Fecha de fin de la reserva
     */
    public constructor (
        public startDate = new Date(),
        public endDate = new Date(),
    ) {}

    /**
     * Formatea un objeto de tipo fecha con el formato establecido para el rango de fechas
     * de la reserva
     *
     * @param date
     */
    private formatDate (date: Date) {
        return moment(date).format(ReservationDateRange.format)
    }

    /**
     * Clave de identificación del rango basado en las fechas del mismo
     */
    public get key () {
        return `${this.formatDate(this.startDate)}-${this.formatDate(this.endDate)}`
    }
}

/**
 * Obtiene un paginación de reservas del servidor
 */
export async function getReservationPagination (input: PaginationInput<ReservationPaginationFilters>) {
    const data = (await client.get('/reservations/reservation-pagination', {
        params: {
            ...(input.filters || new ReservationPaginationFilters()).serialize(),
            recordsPerPage: input.recordsPerPage,
            page: input.page,
            orderBy: _.get(input, 'orderBy.field', 'id'),
            orderByDirection: _.get(input, 'orderBy.direction', 'desc'),
        },
    })).data

    return {
        totalPages: data.totalPages,
        totalRecords: data.totalRecords,
        items: (data.reservations || []).map((i: any) => Reservation.deserialize(i)),
    } as Pagination<Reservation>
}

/**
 * Solicita a la API la reserva asociada al id argumentado
 * @param id
 */
export async function getReservation (id: number) {
    try {
        const data = await fetch(`/reservations/reservation/${id}`)

        return Reservation.deserialize(data)
    } catch (e) {
        throw new Error(_.get(e, 'response.data.message', e.message))
    }
}

/**
 * Registra una reserva en el servidor
 *
 * @param reservation
 */
export async function createReservation (reservation: Reservation)  {
    if (!reservation.isNew) {
        throw new Error('La reserva ya ha sido registrada en el servidor')
    }

    try {
        const data = await post('/reservations/reservation', reservation.serialize())

        return Reservation.deserialize(data)
    } catch (e) {
        throw new Error(_.get(e, 'response.data.message', e.message))
    }
}

/**
 * Elimina la reserva asociada al id argumentado
 *
 * @param id
 */
export async function deleteReservation (id: number) {
    try {
        const data = await remove(`/reservations/reservation/${id}`)

        return data.message === 'success'
    } catch (e) {
        throw ApiError.fromResponse(e)
    }
}

/**
 * Actualiza la información de una reserva
 *
 * @param reservation
 */
export async function updateReservation (reservation: Reservation) {
    if (reservation.isNew) {
        throw Error('No se puede actualizar una reserva que todavía no ha sido registrada')
    }

    try {
        const data = await put(`/reservations/reservation/${reservation.id}`, reservation.serialize())

        return Reservation.deserialize(data)
    } catch (e) {
        throw new Error(_.get(e, 'response.data.message', e.message))
    }
}


/**
 * Obtiene una paginación de departamentos
 *
 * @param input
 */
export async function getDepartmentPagination (input: PaginationInput) {
    const params: KeyValuePair = {
        page: input.page,
        recordsPerPage: input.recordsPerPage,
        orderBy: _.get(input, 'orderBy.field', 'id'),
        orderByDirection: _.get(input, 'orderBy.direction', 'DESC'),
        name: _.get(input, 'filters.searchBy.value', ''),
        type: _.get(input, 'filters.type', null),
    }

    if (input.filters?.orderNumericName) {
        params.orderNumericName = 1;
    }

    if (Array.isArray(input.orderBy)) {
        params.orderBy = input.orderBy.map(o => o.field).join(',')
        params.orderByDirection = input.orderBy.map(o => o.direction).join(',')
    }

    const data = await fetch('/reservations/department-pagination', { params })

    return {
        totalPages: data.totalPages,
        totalRecords: data.totalRecords,
        items: (data.departments || []).map((i: any) => Department.deserialize(i)),
    } as Pagination<Department>
}

/**
 * Crea un nuevo departmento en el servidor
 * @param department
 */
export async function createDepartment (department: Department) {
    try {
        const data = await post('/reservations/department', department.serialize())

        return Department.deserialize(data)
    } catch (e) {
        throw new Error(_.get(e, 'response.data.message', e.message))
    }
}

/**
 * Solicita al servidor al eliminación del departamento con el id argumentado.
 *
 * Es posible que el departamento no pueda ser eliminado si este tiene
 * reservas asociadas. En ese caso la API retornará un error 400
 *
 * @param id
 */
export async function deleteDepartment (id: number) {
    try {
        const result = await remove(`/reservations/department/${id}`)

        return result.sucesss
    } catch (e) {
        throw ApiError.fromResponse(e)
    }
}


export async function listDepartmentTypes () {
    const data = await fetch('/reservations/type-pagination', {})
    if (!Array.isArray(data)) {
        throw new Error('Se esperaba un lista de tipos de departamentos')
    }

    return data.map(dt => DepartmentType.deserialize(dt))
}

/**
 * Crea un nuevo tipo de departamento en el servidor
 * @param type
 */
export async function createDeparmentType (type: DepartmentType) {
    try {
        const data = await post('/reservations/type', type.serialize())

        return DepartmentType.deserialize(data)
    } catch (e) {
        throw new Error(_.get(e, 'response.data.message', e.message))
    }
}

/**
 * Crea un nuevo tipo de departamento en el servidor
 *
 * @param type
 */
export async function updateDepartmentType (type: DepartmentType) {
    if (type.isNew) {
        throw new Error('No de puede actualizar un tipo de departamento que todavía no ha sido registrado')
    }

    try {
        const data = await put(`/reservations/type/${type.id}`, type.serialize())

        return DepartmentType.deserialize(data)
    } catch (e) {
        throw new Error(_.get(e, 'response.data.message', e.message))
    }
}

/**
 * Elimina un tipo de departamento
 *
 * @param type
 */
export async function deleteDepartmentType (type: DepartmentType) {
    if (type.isNew) {
        throw new Error('No se puede eliminar el tipo de departamento porque no tiene ID')
    }

    const response = await remove(`/reservations/type/${type.id}`)

    return (response.data || {}).message === 'success'
}

/**
 * Obtiene el departamento asociado al id argumentado
 *
 * @param id
 */
export async function getDepartment (id: number) {
    try {
        const data = await fetch(`/reservations/department/${id}`)

        return Department.deserialize(data)
    } catch (error) {
        throw new Error(_.get(error, 'response.data.message', error.message))
    }
}

/**
 * Actualizar un departamento
 *
 * @param department
 */
export async function updateDepartment (department: Department) {
    if (department.isNew) {
        throw new Error('No se puede actualizar un departamento que todavía no ha sido registrado')
    }

    try {
        const data = await put(`/reservations/department/${department.id}`, department.serialize())

        return Department.deserialize(data)
    } catch (error) {
        throw new Error(_.get(error, 'response.data.message', error.message))
    }
}

/**
 * Obtiene la lista de servicios
 */
export async function getServices () {
    try {
        const data = await fetch('/reservations/reservation-services-pagination')
        if (!Array.isArray(data)) {
            throw new Error('La lista de servicios obtenida del servidor no es válida')
        }

        return data.map(s => Service.deserialize(s))
    } catch (error) {
        throw new Error(_.get(error, 'response.data.message', error.message))
    }
}

/**
 * Registra un nuevo servicio en la aplicación
 *
 * @param service
 */
export async function createService (service: Service) {
    const data = await post('/reservations/reservation-services', service.serialize())

    return Service.deserialize(data)
}

/**
 * Actualiza un servicio en la aplicación
 *
 * @param service
 */
export async function updateService (service: Service) {
    if (service.isNew) {
        throw new Error('No se puede actualizar un servicio que todavía no ha sido registrado')
    }

    try {
        const data = await put(`/reservations/reservation-services/${service.id}`, service.serialize())

        return Service.deserialize(data)
    } catch (error) {
        throw new Error(_.get(error, 'response.data.message', error.message))
    }
}

/**
 * Elimina un servicio de reservas
 */
export async function deleteService (id: number) {
    try {
        const data = await remove(`/reservations/reservation-services/${id}`)

        return data.message = 'success'
    } catch (error) {
        throw new Error(_.get(error, 'response.data.message', error.message))
    }
}

/**
 * Obtiene la lista de eventos registrados en la aplicación
 */
export async function getEventList () {
    try {
        const data = await fetch('/reservations/event-pagination')
        if (!Array.isArray(data)) {
            throw new Error('Se esperaba una array con la lista de eventos')
        }

        return data.map(e => Event.deserialize(e))
    } catch (error) {
        throw new Error(_.get(error, 'response.data.message', error.message))
    }
}

/**
 * Registra un nuevo evento de reservas en la API
 *
 * @param event
 */
export async function createEvent (event: Event) {
    if (!event.isNew) {
        throw new Error('No se puede crear un evento que ya ha sido registrado')
    }

    try {
        const data = await post('/reservations/event', event.serialize())

        return Event.deserialize(data)
    } catch (e) {
        throw new Error(_.get(e, 'response.data.message', e.message))
    }
}

/**
 * Elimina un evento de reservas
 *
 * @param id
 */
export async function deleteEvent (id: number) {
    try {
        await remove(`/reservations/event/${id}`)

        return true
    } catch (e) {
        throw new Error(_.get(e, 'response.data.message', e.message))
    }
}

/**
 * Actualiza la información de un evento registrado en la aplicación
 *
 * @param event
 */
export async function updateEvent (event: Event) {
    if (event.isNew) {
        throw new Error('No se puede actualizar un event que todavía no sido registrado')
    }

    try {
        const data = await put(`/reservations/event/${event.id}`, event.serialize())

        return Event.deserialize(data)
    } catch (e) {
        throw new Error(_.get(e, 'response.data.message', e.message))
    }
}

/**
 * Disponibilidad de fechas
 *
 * @param dates
 * @param department
 */
export async function getAvailabilityOfDates (dates: ReservationDateRange[], department: Department) {
    const ranges: KeyValuePair = {}
    dates.forEach((range, index) => {
        ranges[`range${index}`] = range.key
    })

    const result = await fetch('/reservations/availability-of-dates', {
        params: {
            department: department.id.toString(),
            ...ranges,
        },
    })

    return result.dates as {[key: string]: boolean}
}

/**
 * Solicita al servidor un precio estimado para una reserva en base al departamento,
 * servicios y fecha argumentados
 *
 * @param descriptor
 */
export async function getReservationPrice (descriptor: GetReservationPriceDescriptor) {
    const data = (await fetch('reservations/reservation-price', {
        params: {
            department: descriptor.department || 0,
            date_start: moment(descriptor.date).format('DD-MM-YYYY HH:mm:ss'),
            services: descriptor.services.length ? descriptor.services.join(',') : '',
        },
    }))

    return data.total as number || 0
}

export async function createReservationList (list: Reservation[]) {
    const data = await post('/reservations/reservation-list', {
        reservations: list.map(r => r.serialize()),
    })

    return CreateListResult.deserialize(data)
}

/**
 * Elimina un lista de reservas
 *
 * @param list
 * @return { number } Numero de reservas eliminadas
 */
export async function deleteReservationList (list: Reservation[]) {
    const result = await remove('/reservations/reservation-list', {
        params: {
            ids: list.map(r => r.id).join(','),
        },
    })

    return result.count as number
}

/**
 * Genera una reserva en el formato de impresión solicitado
 *
 * @param id
 * @param type
 */
export async function printReservation (id: number, type: string) {
   try {
       const data = await fetch('/reservations/reservation-print', {
           params: { reservation: id, type },
       })

       return PrintResult.deserialize(data)
   } catch (error) {
       return PrintResult.fromError(error)
   }
}
